import { SubCard } from './chunk-AL6UBUE7.js';
import { ProtocolIcon } from './chunk-WMBYYAYI.js';
import { MAYAN, MAYAN_FORWARDER_CALLS, MAYAN_COMPONENTS, MAYAN_SWIFT_CALLS, MAYAN_EXECUTOR_CALLS, MAYAN_ORDER_EVENTS, MAYAN_PACKED_PAYLOAD_KIND, MAYAN_SWIFT_MESSAGE_KIND, pickField, MAYAN_FIELD_LABELS, ProtocolValue, SectionCard, FactGrid, legField, ProtocolParamList, ProtocolBadge, decodedOrRaw, isRecord } from './chunk-KRDPBEWT.js';
import { Layers, Send, Waves, FileLock2, KeyRound, Gavel } from 'lucide-react';
import { jsx, jsxs } from 'react/jsx-runtime';
import { MiddleEllipsis, CopyButton } from '@authe/ui-core';

// src/protocols/mayan/executeFacts.ts
function executorLegs(params) {
  for (const p of params) {
    if (p.name && p.name.toLowerCase() === "calls") {
      const v = decodedOrRaw(p.value);
      return Array.isArray(v) ? v : [];
    }
  }
  return [];
}
function innerCall(leg) {
  const decoded = decodedOrRaw(legField(leg, "callData", "data"));
  return isRecord(decoded) ? decoded : void 0;
}
function innerCallLabel(leg) {
  const label = innerCall(leg)?.["label"];
  return typeof label === "string" && label && label !== "unknown" ? label : void 0;
}
function isZeroValue(value) {
  const v = decodedOrRaw(value);
  if (v == null) return true;
  if (typeof v === "number") return v === 0;
  if (typeof v === "bigint") return v === 0n;
  if (typeof v === "string") return v === "" || /^0x?0*$/i.test(v) || v === "0";
  return false;
}
function legCaption(leg, index) {
  return `${index + 1}. ${innerCallLabel(leg) ?? "Call"}`;
}
function MayanExecuteAndRescue({ call: call2, hint }) {
  const legs = executorLegs(call2.params);
  const rescueReceiver = pickField(call2.params, "rescueReceiver");
  const headerFacts = [
    { label: MAYAN_FIELD_LABELS.contract, value: /* @__PURE__ */ jsx(ProtocolValue, { value: call2.address }), when: call2.address },
    {
      label: MAYAN_FIELD_LABELS.calls,
      value: /* @__PURE__ */ jsx("span", { className: "font-mono text-xs", children: legs.length }),
      when: legs.length > 0
    },
    { label: MAYAN_FIELD_LABELS.rescueReceiver, value: /* @__PURE__ */ jsx(ProtocolValue, { value: rescueReceiver }), when: rescueReceiver }
  ];
  const title = /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(ProtocolIcon, { protocolId: MAYAN.protocolId, icon: Layers }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: hint?.label ?? call2.label ?? "executeAndRescue" })
  ] });
  return /* @__PURE__ */ jsxs(
    SectionCard,
    {
      title,
      protocolId: MAYAN.protocolId,
      meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: MAYAN.protocolId }),
      children: [
        /* @__PURE__ */ jsx(FactGrid, { facts: headerFacts }),
        legs.length > 0 && /* @__PURE__ */ jsx("div", { className: "mt-3 flex flex-col gap-2", children: legs.map((leg, i) => {
          const target = legField(leg, "target", "to");
          const value = legField(leg, "value");
          const callData = legField(leg, "callData", "data");
          const legFacts = [
            { label: MAYAN_FIELD_LABELS.target, value: /* @__PURE__ */ jsx(ProtocolValue, { value: target }), when: target },
            { label: MAYAN_FIELD_LABELS.value, value: /* @__PURE__ */ jsx(ProtocolValue, { value }), when: !isZeroValue(value) }
          ];
          return /* @__PURE__ */ jsxs(SubCard, { caption: legCaption(leg, i), children: [
            /* @__PURE__ */ jsx(FactGrid, { facts: legFacts }),
            callData != null && /* @__PURE__ */ jsx("div", { className: "mt-2 min-w-0", children: /* @__PURE__ */ jsx(ProtocolValue, { value: callData }) })
          ] }, i);
        }) }),
        /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: "All call arguments" }),
          /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(ProtocolParamList, { params: call2.params, emptyLabel: "No decoded arguments" }) })
        ] })
      ]
    }
  );
}
function MayanForwarder({ call: call2 }) {
  const tokenIn = pickField(call2.params, "tokenIn", "inputToken", "fromToken", "tokenFrom");
  const amountIn = pickField(call2.params, "amountIn", "inputAmount", "fromAmount");
  const middleToken = pickField(call2.params, "middleToken", "midToken");
  const minMiddle = pickField(call2.params, "minMiddleAmount", "minMidAmount");
  const mayanProtocol = pickField(call2.params, "mayanProtocol");
  const swapProtocol = pickField(call2.params, "swapProtocol");
  const facts = [
    { label: MAYAN_FIELD_LABELS.contract, value: /* @__PURE__ */ jsx(ProtocolValue, { value: call2.address }), when: call2.address },
    { label: MAYAN_FIELD_LABELS.tokenIn, value: /* @__PURE__ */ jsx(ProtocolValue, { value: tokenIn }), when: tokenIn },
    { label: MAYAN_FIELD_LABELS.amountIn, value: /* @__PURE__ */ jsx(ProtocolValue, { value: amountIn }), when: amountIn != null },
    { label: MAYAN_FIELD_LABELS.middleToken, value: /* @__PURE__ */ jsx(ProtocolValue, { value: middleToken }), when: middleToken },
    { label: MAYAN_FIELD_LABELS.minMiddleAmount, value: /* @__PURE__ */ jsx(ProtocolValue, { value: minMiddle }), when: minMiddle != null },
    { label: MAYAN_FIELD_LABELS.swapProtocol, value: /* @__PURE__ */ jsx(ProtocolValue, { value: swapProtocol }), when: swapProtocol },
    { label: MAYAN_FIELD_LABELS.mayanProtocol, value: /* @__PURE__ */ jsx(ProtocolValue, { value: mayanProtocol }), when: mayanProtocol }
  ];
  const title = /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(ProtocolIcon, { protocolId: MAYAN.protocolId, icon: Send }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: call2.label ?? "Mayan Forwarder" })
  ] });
  return /* @__PURE__ */ jsxs(SectionCard, { title, protocolId: MAYAN.protocolId, meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: MAYAN.protocolId }), children: [
    /* @__PURE__ */ jsx(FactGrid, { facts }),
    /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
      /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: "All call arguments" }),
      /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(ProtocolParamList, { params: call2.params, emptyLabel: "No decoded arguments" }) })
    ] })
  ] });
}
function MayanOrderEvent({ event: event2, hint }) {
  const orderHash = pickField(event2.params, "key", "orderHash", "orderId", "keyHash", "orderKey");
  const trader = pickField(event2.params, "trader", "srcAddr", "addrSrc", "sender");
  const recipient = pickField(event2.params, "recipient", "destAddr", "addrDest", "receiver", "to");
  const token = pickField(event2.params, "tokenOut", "token", "tokenIn");
  const amount = pickField(event2.params, "amount", "netAmount", "fulfillAmount", "amountOut");
  const facts = [
    { label: MAYAN_FIELD_LABELS.contract, value: /* @__PURE__ */ jsx(ProtocolValue, { value: event2.address }), when: event2.address },
    { label: MAYAN_FIELD_LABELS.orderHash, value: /* @__PURE__ */ jsx(ProtocolValue, { value: orderHash }), when: orderHash },
    { label: MAYAN_FIELD_LABELS.trader, value: /* @__PURE__ */ jsx(ProtocolValue, { value: trader }), when: trader },
    { label: MAYAN_FIELD_LABELS.recipient, value: /* @__PURE__ */ jsx(ProtocolValue, { value: recipient }), when: recipient },
    { label: MAYAN_FIELD_LABELS.token, value: /* @__PURE__ */ jsx(ProtocolValue, { value: token }), when: token },
    { label: MAYAN_FIELD_LABELS.amount, value: /* @__PURE__ */ jsx(ProtocolValue, { value: amount }), when: amount != null }
  ];
  const title = /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(ProtocolIcon, { protocolId: MAYAN.protocolId, icon: Waves }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: hint.label ?? event2.name ?? "Mayan order" })
  ] });
  return /* @__PURE__ */ jsxs(SectionCard, { title, protocolId: MAYAN.protocolId, meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: MAYAN.protocolId }), children: [
    /* @__PURE__ */ jsx(FactGrid, { facts }),
    /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
      /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: "All event fields" }),
      /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(ProtocolParamList, { params: event2.params, emptyLabel: "No decoded fields" }) })
    ] })
  ] });
}
function MayanPackedPayload({ display, raw }) {
  const label = typeof display === "string" && display ? display : "Packed payload";
  const hex = typeof raw === "string" ? raw : "";
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 flex-wrap items-center gap-1.5 align-middle", children: [
    /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1 rounded-sm bg-muted px-1.5 py-px text-2xs font-medium text-muted-foreground", children: [
      /* @__PURE__ */ jsx(FileLock2, { className: "size-3", "aria-hidden": true }),
      label
    ] }),
    hex && /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 items-center gap-1 font-mono text-2xs text-muted-foreground", children: [
      /* @__PURE__ */ jsx(MiddleEllipsis, { value: hex, kind: "hash" }),
      /* @__PURE__ */ jsx(CopyButton, { value: hex })
    ] })
  ] });
}
function isRecord2(v) {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
function text(v) {
  if (v == null) return "";
  return typeof v === "bigint" ? v.toString() : String(v);
}
function Hex({ value }) {
  const t = text(value);
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 items-center gap-1 align-middle", children: [
    /* @__PURE__ */ jsx(MiddleEllipsis, { value: t, kind: "hash" }),
    /* @__PURE__ */ jsx(CopyButton, { value: t })
  ] });
}
function Address({ value }) {
  return /* @__PURE__ */ jsx(ProtocolValue, { value });
}
function ActionBadge({ name }) {
  return /* @__PURE__ */ jsx("span", { className: "inline-flex items-center rounded-sm bg-primary/10 px-1.5 py-px text-2xs font-medium text-primary", children: name });
}
function orderFacts(o) {
  return [
    { label: MAYAN_FIELD_LABELS.orderHash, value: /* @__PURE__ */ jsx(Hex, { value: o.order_hash }), when: o.order_hash != null },
    { label: MAYAN_FIELD_LABELS.sourceChain, value: text(o.src_chain_name) || text(o.src_chain_id), when: o.src_chain_name ?? o.src_chain_id },
    { label: MAYAN_FIELD_LABELS.token, value: /* @__PURE__ */ jsx(Address, { value: o.token_in }), when: o.token_in != null },
    { label: MAYAN_FIELD_LABELS.recipient, value: /* @__PURE__ */ jsx(Address, { value: o.recipient }), when: o.recipient != null }
  ];
}
function v2Facts(o) {
  const fulfilled = text(o.fulfill_time_iso) || text(o.fulfill_time);
  return [
    { label: MAYAN_FIELD_LABELS.referrer, value: /* @__PURE__ */ jsx(Address, { value: o.referrer }), when: o.referrer != null },
    { label: MAYAN_FIELD_LABELS.referrerBps, value: text(o.referrer_bps), when: o.referrer_bps != null },
    { label: MAYAN_FIELD_LABELS.protocolBps, value: text(o.protocol_bps), when: o.protocol_bps != null },
    { label: MAYAN_FIELD_LABELS.driver, value: /* @__PURE__ */ jsx(Address, { value: o.driver }), when: o.driver != null },
    { label: MAYAN_FIELD_LABELS.fulfillTime, value: fulfilled, when: fulfilled }
  ];
}
function MayanSwiftMessage({ decoded, display }) {
  const m = isRecord2(decoded) ? decoded : {};
  const actionName = text(m.action_name);
  const items = Array.isArray(m.items) ? m.items.filter(isRecord2) : [];
  const facts = [
    { label: MAYAN_FIELD_LABELS.action, value: actionName ? /* @__PURE__ */ jsx(ActionBadge, { name: actionName }) : null, when: !!actionName },
    ...orderFacts(m),
    ...v2Facts(m)
  ];
  const title = /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(ProtocolIcon, { protocolId: MAYAN.protocolId, icon: KeyRound }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: text(display) || "Mayan Swift message" })
  ] });
  return /* @__PURE__ */ jsxs(
    SectionCard,
    {
      title,
      protocolId: MAYAN.protocolId,
      meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: MAYAN.protocolId }),
      children: [
        /* @__PURE__ */ jsx(FactGrid, { facts }),
        items.length > 0 && /* @__PURE__ */ jsx("div", { className: "mt-3 space-y-2", children: items.map((it, i) => /* @__PURE__ */ jsx("div", { className: "rounded-md border border-border/60 bg-muted/20 px-2.5 py-2", children: /* @__PURE__ */ jsx(FactGrid, { facts: orderFacts(it) }) }, text(it.order_hash) || i)) })
      ]
    }
  );
}
function MayanSwiftOrder({ call: call2 }) {
  const trader = pickField(call2.params, "trader", "srcAddr", "addrSrc", "sender");
  const recipient = pickField(call2.params, "destAddr", "recipient", "addrDest", "receiver");
  const token = pickField(call2.params, "tokenIn", "bridgeToken", "token");
  const tokenOut = pickField(call2.params, "tokenOut", "outputToken", "finalToken", "destToken", "toToken");
  const amount = pickField(call2.params, "amount", "amountIn");
  const minAmountOut = pickField(call2.params, "minAmountOut", "amountOutMin", "minReturnAmount", "promisedAmount");
  const destChain = pickField(call2.params, "destChainId", "destChain", "targetChain", "toChainId");
  const referrer = pickField(call2.params, "referrerAddr", "referrer", "referrerAddress");
  const gasDrop = pickField(call2.params, "gasDrop");
  const deadline = pickField(call2.params, "deadline");
  const orderHash = pickField(call2.params, "orderHash", "key", "orderId", "keyHash", "orderKey");
  const facts = [
    { label: MAYAN_FIELD_LABELS.contract, value: /* @__PURE__ */ jsx(ProtocolValue, { value: call2.address }), when: call2.address },
    { label: MAYAN_FIELD_LABELS.orderHash, value: /* @__PURE__ */ jsx(ProtocolValue, { value: orderHash }), when: orderHash },
    { label: MAYAN_FIELD_LABELS.trader, value: /* @__PURE__ */ jsx(ProtocolValue, { value: trader }), when: trader },
    { label: MAYAN_FIELD_LABELS.recipient, value: /* @__PURE__ */ jsx(ProtocolValue, { value: recipient }), when: recipient },
    { label: MAYAN_FIELD_LABELS.token, value: /* @__PURE__ */ jsx(ProtocolValue, { value: token }), when: token },
    { label: MAYAN_FIELD_LABELS.tokenOut, value: /* @__PURE__ */ jsx(ProtocolValue, { value: tokenOut }), when: tokenOut },
    { label: MAYAN_FIELD_LABELS.amount, value: /* @__PURE__ */ jsx(ProtocolValue, { value: amount }), when: amount != null },
    {
      // The auction FLOOR Mayan guarantees crosses — not the (often higher,
      // solver-delivered) settled amount; tag it "(min)" so it's never read as final.
      label: MAYAN_FIELD_LABELS.minAmountOut,
      value: /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
        /* @__PURE__ */ jsx(ProtocolValue, { value: minAmountOut }),
        /* @__PURE__ */ jsx("span", { className: "text-2xs uppercase tracking-wide text-muted-foreground", title: "Guaranteed minimum (floor) \u2014 the delivered amount is at least this.", children: "(min)" })
      ] }),
      when: minAmountOut != null
    },
    { label: MAYAN_FIELD_LABELS.destChain, value: /* @__PURE__ */ jsx(ProtocolValue, { value: destChain }), when: destChain != null },
    { label: MAYAN_FIELD_LABELS.gasDrop, value: /* @__PURE__ */ jsx(ProtocolValue, { value: gasDrop }), when: gasDrop != null },
    { label: MAYAN_FIELD_LABELS.referrer, value: /* @__PURE__ */ jsx(ProtocolValue, { value: referrer }), when: referrer },
    { label: MAYAN_FIELD_LABELS.deadline, value: /* @__PURE__ */ jsx(ProtocolValue, { value: deadline }), when: deadline != null }
  ];
  const title = /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(ProtocolIcon, { protocolId: MAYAN.protocolId, icon: Gavel }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: call2.label ?? "Swift order" })
  ] });
  return /* @__PURE__ */ jsxs(SectionCard, { title, protocolId: MAYAN.protocolId, meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: MAYAN.protocolId }), children: [
    /* @__PURE__ */ jsx(FactGrid, { facts }),
    /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
      /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: "All call arguments" }),
      /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(ProtocolParamList, { params: call2.params, emptyLabel: "No decoded arguments" }) })
    ] })
  ] });
}

// src/protocols/mayan/index.ts
var PID = MAYAN.protocolId;
var C = MAYAN_COMPONENTS;
var call = (name, component) => ({
  protocolId: PID,
  call: name,
  component,
  summaryFields: []
});
var event = (name, component) => ({
  protocolId: PID,
  event: name,
  component,
  summaryFields: []
});
var MAYAN_HINTS = {
  valueHints: [
    { protocolId: PID, kind: MAYAN_PACKED_PAYLOAD_KIND, component: C.packedPayload, fields: [] },
    { protocolId: PID, kind: MAYAN_SWIFT_MESSAGE_KIND, component: C.swiftMessage, fields: [] }
  ],
  eventHints: MAYAN_ORDER_EVENTS.map((name) => event(name, C.orderEvent)),
  callHints: [
    ...MAYAN_FORWARDER_CALLS.map((name) => call(name, C.forwarder)),
    ...MAYAN_SWIFT_CALLS.map((name) => call(name, C.swiftOrder)),
    ...MAYAN_EXECUTOR_CALLS.map((name) => call(name, C.executor))
  ]
};
function registerMayan(registry) {
  registry.registerCall(C.forwarder, MayanForwarder);
  registry.registerCall(C.swiftOrder, MayanSwiftOrder);
  registry.registerCall(C.executor, MayanExecuteAndRescue);
  registry.registerEvent(C.orderEvent, MayanOrderEvent);
  registry.registerValue(C.packedPayload, MayanPackedPayload);
  registry.registerValue(C.swiftMessage, MayanSwiftMessage);
  registry.registerHints(MAYAN_HINTS);
  return registry;
}

export { MAYAN_HINTS, MayanExecuteAndRescue, MayanForwarder, MayanOrderEvent, MayanPackedPayload, MayanSwiftMessage, MayanSwiftOrder, registerMayan };
//# sourceMappingURL=chunk-3QIPNBYU.js.map
//# sourceMappingURL=chunk-3QIPNBYU.js.map